import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from './AuthContext';

export type Theme = 'classic' | 'smart';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [theme, setThemeState] = useState<Theme>('classic');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Load theme from localStorage (we'll add backend sync later)
    const savedTheme = localStorage.getItem('app_theme') as Theme;
    if (savedTheme && (savedTheme === 'classic' || savedTheme === 'smart')) {
      setThemeState(savedTheme);
    }
    setIsLoading(false);
  }, [user]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem('app_theme', newTheme);
    // TODO: Sync with backend user settings when that API is ready
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, isLoading }}>{children}</ThemeContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
